import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Checkbox,
  Badge,
  Divider,
  Space,
} from "antd";
import moment from "moment";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import {
  PrinterOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { API } from "../../Config";
import { GET, POST, PUT } from "../../Config/ApiRequests";
import "./style.css";

const { RangePicker } = DatePicker;

const FormModal = ({
  visible,
  onClose,
  mode = "post",
  item,
  page_mode,
  columnsVisible,
  accounts = [],
  defaultCurrency,
  onchange,
}) => {
  const { id: userId } = useSelector((state) => state.Auth.user);
  const [form] = Form.useForm();
  const printRef = useRef();

  const [loading, setLoading] = useState(false);
  const [agentData, setAgentData] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(item?.voucherDetails?.[0]?.account_no);
  const [selectedType, setSelectedType] = useState(() => {
    if (mode === "put") {
      if (item?.voucher_type === "OP") return "other";
      if (item?.purchase_id) return "purchase";
    }
    return "awb";
  });
  const [selectedItems, setSelectedItems] = useState({});
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showNewItems, setShowNewItems] = useState(false); // Toggle to show new items in edit mode
  const [otherItems, setOtherItems] = useState(() => {
    if (mode === "put" && item?.voucher_type === "OP" && item?.voucherDetails) {
      return item.voucherDetails.map((d, i) => ({
        id: d.key || i + 1,
        account_no: d.account_no || "",
        description: d.description || "",
        amount: d.amount_cr?.toString() || "",
      }));
    }
    return [{ id: 1, account_no: "", description: "", amount: "" }];
  });

  const isAwb = selectedType === "awb";
  const isOther = selectedType === "other";
  const voucherKeys = useMemo(
    () => (item?.voucherDetails || []).map((d) => String(d.key)),
    [item?.voucherDetails]
  );

  // Get max allowed amount for an item
  const getMaxAllowed = useCallback(
    (child) => {
      if (!child) return 0;
      const amt = Number(isAwb ? child.amount : child.total) || 0;
      const paid = Number(child.paidAmount) || 0;
      let max = amt - paid;
      if (mode === "put") {
        const vd = item?.voucherDetails?.find((v) => String(v.key) === String(child.id));
        max += Number(vd?.amount_cr) || 0;
      }
      return max;
    },
    [isAwb, mode, item?.voucherDetails]
  );

  // Transform API data - FILTER OUT FULLY PAID + DATE & SEARCH FILTERS
  const parentData = useMemo(() => {
    if (!agentData?.data || isOther) return [];

    const source = isAwb ? agentData.data.awbPayments : agentData.data.purchase;
    if (!source) return [];

    return source
      .map((p) => {
        // Date filter - check parent date
        // AWB: check payment_date, date, awb.date, awb.awb_date, createdAt
        // Purchase: check payment_date, purchase_date, createdAt
        const parentDate = isAwb
          ? p.payment_date || p.date || p.awb?.date || p.awb?.awb_date || p.awb?.flight_date || p.createdAt || p.created_at
          : p.payment_date || p.purchase_date || p.createdAt || p.created_at;

        if (dateRange && dateRange[0] && dateRange[1] && parentDate) {
          const itemDate = moment(parentDate);
          if (itemDate.isValid()) {
            const startDate = moment(dateRange[0]).startOf("day");
            const endDate = moment(dateRange[1]).endOf("day");
            if (!itemDate.isBetween(startDate, endDate, null, "[]")) {
              return null;
            }
          }
        }

        const rawChildren = isAwb ? p.awbPaymentDetails : p.purchaseDetails;
        // In edit mode, show ALL items (not just existing voucher items) so user can add more
        const allChildren = (rawChildren || [])
          .map((c) => ({
            ...c,
            paidAmount: isAwb ? c.paidAmount : c.paid_amount,
            awb: isAwb ? p.awb : null,
            AwbPayment_id: isAwb ? p.id : null,
            purchase_id: isAwb ? null : p.id,
            account_no: isAwb ? c.account_no : p.supplierDetails?.account_no,
            supplier_name: isAwb ? null : p.supplierDetails?.account_name,
            parentName: isAwb
              ? `${p.awb?.airline || ""} - ${p.awb?.awb_no || ""}`
              : p.description || `Purchase #${p.id}`,
            // Flag to identify existing voucher items in edit mode
            isExistingItem: mode === "put" && voucherKeys.includes(String(c.id)),
          }));

        // Filter children based on mode and showNewItems toggle
        const children = allChildren.filter((c) => {
          const amt = Number(isAwb ? c.amount : c.total) || 0;
          const paid = Number(c.paidAmount) || 0;
          let max = amt - paid;
          if (mode === "put") {
            const vd = item?.voucherDetails?.find((v) => String(v.key) === String(c.id));
            max += Number(vd?.amount_cr) || 0;
          }
          // In edit mode without showNewItems, only show existing items
          if (mode === "put" && !showNewItems && !c.isExistingItem) {
            return false;
          }
          return max > 0;
        });

        if (children.length === 0) return null;

        const totalAmt = children.reduce((s, c) => s + Number(isAwb ? c.amount : c.total) || 0, 0);
        const totalPaid = children.reduce((s, c) => s + Number(c.paidAmount) || 0, 0);

        const name = isAwb
          ? `${p.awb?.airline || ""} - ${p.awb?.awb_no || ""}`
          : p.description || `Purchase #${p.id}`;
        const subtext = isAwb
          ? `${p.awb?.orijin_airport_name || ""} → ${p.awb?.destination_airport_name || ""}`
          : p.supplierDetails?.account_name || "";

        // Search filter - check name, subtext, awb_no, etc.
        if (searchText) {
          const searchLower = searchText.toLowerCase();
          const searchMatch =
            name.toLowerCase().includes(searchLower) ||
            subtext.toLowerCase().includes(searchLower) ||
            (p.awb?.awb_no || "").toLowerCase().includes(searchLower) ||
            (p.awb?.airline || "").toLowerCase().includes(searchLower) ||
            children.some(
              (c) =>
                (c.name || "").toLowerCase().includes(searchLower) ||
                (c.description || "").toLowerCase().includes(searchLower)
            );
          if (!searchMatch) return null;
        }

        return {
          key: `${isAwb ? "awb" : "pur"}-${p.id}`,
          name,
          subtext,
          date: parentDate,
          totalAmt,
          totalPaid,
          remaining: totalAmt - totalPaid,
          items: children,
        };
      })
      .filter(Boolean);
  }, [agentData, isAwb, isOther, mode, voucherKeys, item?.voucherDetails, dateRange, searchText, showNewItems]);

  // Get selected items data for the bottom table
  const selectedItemsData = useMemo(() => {
    const result = [];
    parentData.forEach((p) =>
      (p.items || []).forEach((c) => {
        if (selectedItems[c.id]) {
          result.push({ ...c, parentName: p.name });
        }
      })
    );
    return result;
  }, [parentData, selectedItems]);

  // Initialize edit mode
  useEffect(() => {
    if (mode !== "put" || !parentData.length || !item?.voucherDetails) return;

    const sel = {}, amt = {}, exp = [];
    parentData.forEach((p) => {
      let hasSelected = false;
      (p.items || []).forEach((c) => {
        const vd = item.voucherDetails.find((v) => String(v.key) === String(c.id));
        if (vd) {
          sel[c.id] = true;
          amt[c.id] = Number(vd.amount_cr) || 0;
          hasSelected = true;
        }
      });
      if (hasSelected) exp.push(p.key);
    });

    setSelectedItems(sel);
    setPaymentAmounts(amt);
    setExpandedKeys(exp);
  }, [mode, parentData, item?.voucherDetails]);

  // Reset on type change
  useEffect(() => {
    setSelectedItems({});
    setPaymentAmounts({});
    setExpandedKeys([]);
    setDateRange(null);
    setSearchText("");
    setShowNewItems(false);
    if (isOther && mode !== "put") {
      setOtherItems([{ id: 1, account_no: "", description: "", amount: "" }]);
    }
  }, [selectedType, mode, isOther]);

  // Fetch agent data
  useEffect(() => {
    if (!selectedAgent || isOther) return;
    GET(`${API.GET_AGENT_REGISTRATION_LIST}${selectedAgent}/${mode}`)
      .then((res) => setAgentData(res))
      .catch((error) => message.error(error.message));
  }, [selectedAgent, isOther, mode]);

  // Handle item selection
  const onSelectItem = useCallback(
    (id, checked, child) => {
      setSelectedItems((prev) => ({ ...prev, [id]: checked }));
      if (checked && !paymentAmounts[id]) {
        const max = getMaxAllowed(child);
        setPaymentAmounts((prev) => ({ ...prev, [id]: max > 0 ? max : 0 }));
      }
    },
    [paymentAmounts, getMaxAllowed]
  );

  // Handle payment change
  const onPaymentChange = useCallback(
    (id, value, child) => {
      const max = getMaxAllowed(child);
      const val = Math.min(Math.max(parseFloat(value) || 0, 0), max);
      setPaymentAmounts((prev) => ({ ...prev, [id]: val }));
    },
    [getMaxAllowed]
  );

  // Select all in parent
  const onSelectAllInParent = useCallback(
    (parent, checked) => {
      const updates = {}, amtUpdates = {};
      (parent?.items || []).forEach((c) => {
        const max = getMaxAllowed(c);
        if (max > 0) {
          updates[c.id] = checked;
          if (checked && !paymentAmounts[c.id]) amtUpdates[c.id] = max;
        }
      });
      setSelectedItems((prev) => ({ ...prev, ...updates }));
      if (checked) setPaymentAmounts((prev) => ({ ...prev, ...amtUpdates }));
    },
    [getMaxAllowed, paymentAmounts]
  );

  // Remove from selected
  const onRemoveSelected = useCallback((id) => {
    setSelectedItems((prev) => ({ ...prev, [id]: false }));
  }, []);

  // Calculate total
  const total = useMemo(
    () =>
      Object.entries(selectedItems).reduce(
        (sum, [id, sel]) => (sel ? sum + (Number(paymentAmounts[id]) || 0) : sum),
        0
      ),
    [selectedItems, paymentAmounts]
  );

  // Helper to get payment status
  const getPaymentStatus = useCallback((paid, total) => {
    if (paid >= total) return { text: "Paid", color: "green" };
    if (paid > 0) return { text: "Partial", color: "orange" };
    return { text: "Unpaid", color: "red" };
  }, []);

  // Parent columns
  const parentCols = useMemo(
    () => [
      {
        title: isAwb ? "Airway Bill" : "Purchase",
        key: "name",
        render: (_, r) => (
          <div>
            <strong>{r?.name}</strong>
            {r?.subtext && <div style={{ fontSize: 12, color: "#666" }}>{r.subtext}</div>}
          </div>
        ),
      },
      {
        title: "Date",
        dataIndex: "date",
        width: 100,
        align: "center",
        render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
      },
      {
        title: "Items",
        dataIndex: "items",
        width: 70,
        align: "center",
        render: (c) => <Badge count={c?.length || 0} showZero color="#1890ff" />,
      },
      {
        title: "Total",
        dataIndex: "totalAmt",
        width: 100,
        align: "right",
        render: (v) => (v || 0).toFixed(2),
      },
      {
        title: "Paid",
        dataIndex: "totalPaid",
        width: 100,
        align: "right",
        render: (v) => (v || 0).toFixed(2),
      },
      {
        title: "Remaining",
        dataIndex: "remaining",
        width: 100,
        align: "right",
        render: (v) => <span style={{ color: "#f5222d" }}>{(v || 0).toFixed(2)}</span>,
      },
      {
        title: "Payment",
        width: 80,
        align: "center",
        render: (_, r) => {
          const status = getPaymentStatus(r?.totalPaid || 0, r?.totalAmt || 0);
          return <Tag color={status.color}>{status.text}</Tag>;
        },
      },
      {
        title: "Selected",
        width: 90,
        align: "center",
        render: (_, r) => {
          const count = (r?.items || []).filter((c) => selectedItems[c?.id]).length;
          if (count > 0) return <Tag color="blue">{count} Selected</Tag>;
          return <span style={{ color: "#999" }}>-</span>;
        },
      },
    ],
    [isAwb, selectedItems, getPaymentStatus]
  );

  // Child columns for expanded row (simplified - just selection)
  const renderChildTable = useCallback(
    (parent) => {
      const items = parent?.items || [];
      const allSelected = items.length > 0 && items.every((c) => selectedItems[c?.id]);
      const someSelected = items.some((c) => selectedItems[c?.id]);

      const childCols = [
      {
          title: (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => onSelectAllInParent(parent, e.target.checked)}
            />
          ),
          width: 40,
        align: "center",
          render: (_, c) => (
            <Checkbox
              checked={!!selectedItems[c?.id]}
              onChange={(e) => onSelectItem(c?.id, e.target.checked, c)}
            />
          ),
        },
        // Show "Existing" badge in edit mode for items already in voucher
        mode === "put" && {
          title: "Type",
          width: 70,
          align: "center",
          render: (_, c) => (
            c.isExistingItem ? (
              <Tag color="blue" style={{ margin: 0 }}>Existing</Tag>
            ) : (
              <Tag color="green" style={{ margin: 0 }}>New</Tag>
            )
          ),
        },
        // Item Name column - only for Purchase
        !isAwb && {
          title: "Item Name",
          dataIndex: "name",
          render: (t) => t || "-",
        },
        {
          title: "Description",
          dataIndex: "description",
          render: (t) => t || "-",
        },
        {
          title: "Amount",
          dataIndex: isAwb ? "amount" : "total",
          width: 100,
          align: "right",
          render: (v) => (Number(v) || 0).toFixed(2),
        },
        {
          title: "Paid",
          dataIndex: "paidAmount",
          width: 100,
          align: "right",
          render: (v) => (Number(v) || 0).toFixed(2),
        },
        {
          title: "Remaining",
          width: 100,
          align: "right",
          render: (_, c) => {
            const amt = Number(isAwb ? c?.amount : c?.total) || 0;
            const paid = Number(c?.paidAmount) || 0;
            return <span style={{ color: "#f5222d" }}>{(amt - paid).toFixed(2)}</span>;
          },
        },
      {
        title: "Status",
          width: 80,
        align: "center",
          render: (_, c) => {
            const amt = Number(isAwb ? c?.amount : c?.total) || 0;
            const paid = Number(c?.paidAmount) || 0;
            const status = getPaymentStatus(paid, amt);
            return <Tag color={status.color}>{status.text}</Tag>;
          },
        },
      ].filter(Boolean);

      return (
        <Table
          dataSource={items}
          rowKey="id"
          pagination={false}
          size="small"
          columns={childCols}
        />
      );
    },
    [isAwb, selectedItems, onSelectItem, onSelectAllInParent, getPaymentStatus]
  );

  // Selected items table columns
  const selectedCols = useMemo(
    () =>
      [
      {
        title: "#",
          width: 40,
        align: "center",
          render: (_, __, i) => i + 1,
      },
        // Show "Existing/New" badge in edit mode
        mode === "put" && {
          title: "Type",
          width: 70,
        align: "center",
          render: (_, c) => (
            c.isExistingItem ? (
              <Tag color="blue" style={{ margin: 0 }}>Existing</Tag>
            ) : (
              <Tag color="green" style={{ margin: 0 }}>New</Tag>
            )
          ),
        },
        {
          title: isAwb ? "AWB" : "Purchase",
          dataIndex: "parentName",
          width: 180,
          render: (t) => <strong>{t}</strong>,
        },
        // Item Name column - only for Purchase
        !isAwb && {
          title: "Item Name",
          dataIndex: "name",
          width: 150,
          render: (t) => t || "-",
      },
      {
        title: "Description",
          dataIndex: "description",
          render: (t) => t || "-",
      },
      {
        title: "Amount",
          width: 100,
          align: "right",
          render: (_, c) => (Number(isAwb ? c?.amount : c?.total) || 0).toFixed(2),
        },
        {
          title: "Pay Amount",
          width: 130,
        align: "center",
          render: (_, c) => (
          <Input
            size="small"
              type="number"
              value={paymentAmounts[c?.id] ?? ""}
              onChange={(e) => onPaymentChange(c?.id, e.target.value, c)}
              style={{ textAlign: "right" }}
          />
        ),
      },
      {
          title: "",
          width: 50,
        align: "center",
          render: (_, c) => (
          <Button
            type="text"
            danger
              size="small"
            icon={<DeleteOutlined />}
              onClick={() => onRemoveSelected(c?.id)}
          />
        ),
      },
      ].filter(Boolean),
    [isAwb, mode, paymentAmounts, onPaymentChange, onRemoveSelected]
  );

  // Other payment handlers
  const onOtherChange = (id, field, value) => {
    setOtherItems((prev) => prev.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
  };

  const addOtherItem = () => {
    const newId = Math.max(...otherItems.map((i) => i.id), 0) + 1;
    setOtherItems((prev) => [...prev, { id: newId, account_no: "", description: "", amount: "" }]);
  };

  const removeOtherItem = (id) => {
    if (otherItems.length > 1) setOtherItems((prev) => prev.filter((i) => i.id !== id));
  };

  // Submit
  const onSubmit = async (values) => {
    if (isOther) {
      const valid = otherItems.filter((i) => i.account_no && i.description && i.amount);
      if (!valid.length) return message.warning("Add at least one payment item");
      if (valid.reduce((s, i) => s + Number(i.amount || 0), 0) <= 0)
        return message.warning("Total must be > 0");
    } else {
      if (!selectedItemsData.length) return message.warning("Select at least one item");
      if (total <= 0) return message.warning("Total amount must be > 0");
    }

    setLoading(true);
    try {
      let voucherItems, crTotal, purchaseId = null;

      if (isOther) {
        const valid = otherItems.filter((i) => i.account_no && i.description && i.amount);
        voucherItems = valid.map((i) => ({
          key: i.id,
          account_no: i.account_no,
          description: i.description,
          amount_cr: Number(i.amount),
          amount_dr: Number(i.amount),
          payment_amount: Number(i.amount),
        }));
        crTotal = valid.reduce((s, i) => s + Number(i.amount || 0), 0);
      } else {
        voucherItems = selectedItemsData.map((c) => ({
          key: c.id,
          account_no: c.account_no,
          description: isAwb ? c.description : `${c.description || ""} - ${c.name || ""}`,
          amount_cr: paymentAmounts[c.id] || 0,
          amount_dr: paymentAmounts[c.id] || 0,
          payment_amount: paymentAmounts[c.id] || 0,
          airway_bill: isAwb ? c.AwbPayment_id : null,
          airway_bill_payment_id: isAwb ? c.awb?.id : null,
          awb_payment_id: isAwb ? c.AwbPayment_id : null,
          purchase_id: isAwb ? null : (c.purchase_id ? Number(c.purchase_id) : null),
          purchase_detail_id: isAwb ? null : c.id,
          supplier_name: c.supplier_name,
          airwaybill_and_airline: isAwb ? `${c.awb?.airline} - ${c.awb?.awb_no}` : "",
        }));
        crTotal = total;
        if (!isAwb && selectedItemsData.length) purchaseId = selectedItemsData[0].purchase_id ? Number(selectedItemsData[0].purchase_id) : null;
      }

      const voucherType = isOther ? "OP" : isAwb ? page_mode : "PV";
      const agentAcc = accounts.find((a) => a.account_no === selectedAgent);

      const payload = {
        voucher_date: values.voucher_date,
        voucher_type: voucherType,
        account_no: values.account_no ?? null,
        agentName: agentAcc?.account_name || null,
        description: values.description ?? "",
        userid: Number(userId),
        cr_total: crTotal,
        dr_total: crTotal,
        total_paid_amount: crTotal,
        purchase_id: purchaseId ? Number(purchaseId) : null,
        voucherItems,
      };

      const url =
        mode === "post"
          ? `${API.VOUCHER_ACTIONS}${voucherType}`
          : `${API.VOUCHER_ACTIONS}${item.id}`;

      if (mode === "post") {
        await POST(url, payload);
      } else {
        await PUT(url, payload);
      }
      message.success("Success");
        onchange();
        onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccName = (no) => {
    const acc = accounts.find((a) => a.account_no === no);
    return acc ? `${acc.account_no} - ${acc.account_name}` : no || "-";
  };

    return (
      <Modal
        title={columnsVisible?.page}
        open={visible}
        onCancel={onClose}
      width={1100}
      footer={null}
      centered
      >
        <Form
          form={form}
        onFinish={onSubmit}
          initialValues={{
          voucher_date: mode === "put" && item?.voucher_date ? moment(item.voucher_date) : moment(),
            description: item?.description,
            account_no: item?.account_no,
          agent: item?.voucherDetails?.[0]?.account_no,
            type: selectedType,
          }}
        >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="account_no" label="Account" labelCol={{ span: 6 }} rules={[{ required: true }]}>
                  <Select
                    size="small"
                    showSearch
                    allowClear
                placeholder="Select account"
                disabled={mode === "put"}
                filterOption={(i, o) => (o?.children?.toString() || "").toLowerCase().includes(i.toLowerCase())}
                  >
                {accounts.map((a) => (
                  <Select.Option key={a.account_no} value={a.account_no}>
                    {a.account_no} - {a.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

            {!isOther && (
              <Form.Item name="agent" label="Agent" labelCol={{ span: 6 }} rules={[{ required: true }]}>
                  <Select
                    size="small"
                    showSearch
                    allowClear
                  placeholder="Select agent"
                    onChange={setSelectedAgent}
                  disabled={mode === "put"}
                  filterOption={(i, o) => (o?.children?.toString() || "").toLowerCase().includes(i.toLowerCase())}
                  >
                  {accounts.map((a) => (
                    <Select.Option key={a.account_no} value={a.account_no}>
                      {a.account_no} - {a.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

            <Form.Item name="type" label="Type" labelCol={{ span: 6 }} rules={[{ required: true }]}>
              <Select size="small" onChange={setSelectedType} disabled={mode === "put"}>
                  <Select.Option value="awb">AWB</Select.Option>
                  <Select.Option value="purchase">Purchase</Select.Option>
                  <Select.Option value="other">Other Payment</Select.Option>
                </Select>
              </Form.Item>
            </Col>

          <Col span={12}>
            <Form.Item name="voucher_date" label="Date" labelCol={{ span: 6 }} rules={[{ required: true }]}>
              <DatePicker size="small" format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            <Form.Item name="description" label="Description" labelCol={{ span: 6 }} rules={[{ required: true, message: "Description is required" }]}>
              <Input.TextArea rows={2} size="small" />
              </Form.Item>
            </Col>
          </Row>

        <div style={{ marginTop: 16 }}>
          {isOther ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <strong>Payment Items</strong>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addOtherItem}>Add</Button>
                </div>
                <Table
                dataSource={otherItems}
                rowKey="id"
                  pagination={false}
                  size="small"
                columns={[
                  { title: "#", width: 40, render: (_, __, i) => i + 1 },
                  {
                    title: "Account",
                    width: 300,
                    render: (_, r) => (
                      <Select size="small" style={{ width: "100%" }} value={r.account_no || undefined}
                        onChange={(v) => onOtherChange(r.id, "account_no", v)} showSearch allowClear
                        filterOption={(i, o) => (o?.children?.toString() || "").toLowerCase().includes(i.toLowerCase())}>
                        {accounts.map((a) => (
                          <Select.Option key={a.account_no} value={a.account_no}>
                            {a.account_no} - {a.account_name}
                          </Select.Option>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: "Description",
                    render: (_, r) => (
                      <Input size="small" value={r.description} onChange={(e) => onOtherChange(r.id, "description", e.target.value)} />
                    ),
                  },
                  {
                    title: "Amount",
                    width: 120,
                    render: (_, r) => (
                      <Input size="small" type="number" value={r.amount} onChange={(e) => onOtherChange(r.id, "amount", e.target.value)} />
                    ),
                  },
                  {
                    title: "",
                    width: 50,
                    render: (_, r) => (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeOtherItem(r.id)} disabled={otherItems.length === 1} />
                    ),
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={3}><strong>Total</strong></Table.Summary.Cell>
                    <Table.Summary.Cell><strong>{otherItems.reduce((s, i) => s + Number(i.amount || 0), 0).toFixed(2)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                )}
              />
            </>
            ) : (
              <>
              {/* AWB/Purchase Selection Table */}
              <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <Space>
                  <strong>{isAwb ? "Select Airway Bills" : "Select Purchases"}</strong>
                  {mode === "put" && (
                    <Checkbox
                      checked={showNewItems}
                      onChange={(e) => setShowNewItems(e.target.checked)}
                    >
                      <span style={{ color: "#1890ff" }}>Add New Items</span>
                    </Checkbox>
                  )}
                </Space>
                <Space wrap>
                  <Input
                    placeholder="Search AWB, airline, description..."
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 250 }}
                      size="small"
                    />
                  <RangePicker
                    size="small"
                    format="DD/MM/YYYY"
                    value={dateRange}
                    onChange={setDateRange}
                    placeholder={["From Date", "To Date"]}
                    allowClear
                    style={{ width: 220 }}
                  />
                </Space>
                </div>
              <Table
                dataSource={parentData}
                columns={parentCols}
                rowKey="key"
                pagination={false}
                size="small"
                expandable={{
                  expandedRowRender: renderChildTable,
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: setExpandedKeys,
                  expandIcon: ({ expanded, onExpand, record }) => (
                    <Button type="text" size="small" icon={expanded ? <DownOutlined /> : <RightOutlined />} onClick={(e) => onExpand(record, e)} />
                  ),
                }}
                locale={{
                  emptyText: !selectedAgent
                    ? "Select an agent"
                    : searchText || dateRange
                    ? "No items match the filters"
                    : "No pending items",
                }}
              />

              {/* Selected Items Table */}
              {selectedItemsData.length > 0 && (
                <>
                  <Divider style={{ margin: "16px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <strong>Selected Items ({selectedItemsData.length})</strong>
                    <span>Total: <strong style={{ color: "#1890ff", fontSize: 16 }}>{total.toFixed(2)}</strong></span>
          </div>
              <Table
                    dataSource={selectedItemsData}
                    columns={selectedCols}
                    rowKey="id"
                pagination={false}
                    size="small"
                bordered
                    summary={() => (
                    <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={(isAwb ? 4 : 5) + (mode === "put" ? 1 : 0)}><strong>Total Amount</strong></Table.Summary.Cell>
                      <Table.Summary.Cell align="center">
                          <strong style={{ color: "#1890ff" }}>{total.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                        <Table.Summary.Cell />
                    </Table.Summary.Row>
                    )}
              />
                </>
              )}
            </>
          )}
            </div>

        <Row justify="end" style={{ marginTop: 16 }} gutter={8}>
          <Col>
            <ReactToPrint trigger={() => <Button icon={<PrinterOutlined />}>Print</Button>} content={() => printRef.current} />
          </Col>
          <Col><Button onClick={onClose}>Cancel</Button></Col>
          <Col><Button type="primary" htmlType="submit" loading={loading}>{mode === "post" ? "Save" : "Update"}</Button></Col>
        </Row>
      </Form>

      {/* Print */}
      <div style={{ display: "none" }}>
        <div ref={printRef} style={{ padding: 20 }}>
          <h2 style={{ textAlign: "center" }}>{columnsVisible?.page}</h2>
          <p><strong>Date:</strong> {form.getFieldValue("voucher_date")?.format("DD/MM/YYYY")}</p>
          <p><strong>Account:</strong> {getAccName(form.getFieldValue("account_no"))}</p>
          {!isOther && <p><strong>Agent:</strong> {getAccName(form.getFieldValue("agent"))}</p>}
          <p><strong>Description:</strong> {form.getFieldValue("description")}</p>
                    <Table
                      size="small"
                      pagination={false}
                      bordered
                      rowKey="id"
            dataSource={isOther ? otherItems.filter((i) => i.account_no && i.amount) : selectedItemsData}
            columns={
              isOther
                ? [
                    { title: "#", render: (_, __, i) => i + 1 },
                    { title: "Account", dataIndex: "account_no", render: getAccName },
                    { title: "Description", dataIndex: "description" },
                    { title: "Amount", dataIndex: "amount", align: "right", render: (v) => Number(v || 0).toFixed(2) },
                  ]
                : [
                    { title: "#", render: (_, __, i) => i + 1 },
                    { title: isAwb ? "AWB" : "Purchase", dataIndex: "parentName" },
                    // Item Name column - only for Purchase
                    !isAwb && { title: "Item Name", dataIndex: "name" },
                    { title: "Description", dataIndex: "description" },
                    { title: "Pay Amount", align: "right", render: (_, c) => (paymentAmounts[c.id] || 0).toFixed(2) },
                  ].filter(Boolean)
            }
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={isOther ? 3 : isAwb ? 3 : 4}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>{(isOther ? otherItems.filter((i) => i.account_no && i.amount).reduce((s, i) => s + Number(i.amount || 0), 0) : total).toFixed(2)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          </div>
        </div>
    </Modal>
  );
};

export default FormModal;
